import React from "react";

import {
	FlexGrid,
	InfoMessage,
	TimePicker,
	Title,
} from "@amphore-dev/amphore-lib";
import { Form, Formik } from "formik";
import { t } from "i18next";

import { DaySettings } from "@/components/molecules";

import { weeklyTotals } from "@/utils/UWorkDays";

import { TDayName, TOnboardingRhythmValues } from "@/types";

import { OnboardingStepCard } from "../components/OnboardingStepCard";

interface IRhythmStepProps {
	initialValues: TOnboardingRhythmValues;
	onBack: () => void;
	onSkip: () => void;
	onNext: (values: TOnboardingRhythmValues) => void;
}

export const RhythmStep: React.FC<IRhythmStepProps> = ({
	initialValues,
	onBack,
	onSkip,
	onNext,
}) => {
	return (
		<Formik
			initialValues={initialValues}
			onSubmit={(values) => onNext(values)}
		>
			{({ values, setFieldValue, submitForm }) => {
				const { hours, days } = weeklyTotals(values);
				const handleDayChange = (
					day: TDayName,
					dayHours: string,
					isWorkingDay: boolean
				) => {
					// A day switched on with no hours yet ("00:00" is what
					// DaySettings reports for an unset day) inherits the daily
					// default, so the weekly total is right without touching
					// each row.
					const unset = !dayHours || dayHours === "00:00";
					setFieldValue(`workDays.${day}`, {
						hours:
							isWorkingDay && unset
								? values.hoursPerDay
								: dayHours,
						isWorkingDay,
					});
				};
				return (
					<OnboardingStepCard
						step="rhythm"
						title={t("onboarding.steps.rhythm.title")}
						intro={t("onboarding.steps.rhythm.intro")}
						onBack={onBack}
						onSkip={onSkip}
						onNext={submitForm}
					>
						<Form>
							<FlexGrid
								columns={2}
								minItemWidth="260px"
								gap="2rem"
							>
								<div className="flex flex-col gap-4">
									<Title as="h2" size="h5">
										{t("forms.rhythm.workingDays")}
									</Title>
									<div className="flex flex-col gap-1">
										{(
											Object.keys(
												values.workDays
											) as TDayName[]
										).map((day) => (
											<DaySettings
												key={day}
												day={day}
												hours={
													values.workDays[day].hours
												}
												isWorkingDay={
													values.workDays[day]
														.isWorkingDay
												}
												onChange={handleDayChange}
											/>
										))}
									</div>
								</div>
								<div className="flex flex-col gap-4">
									<Title as="h2" size="h5">
										{t("onboarding.steps.rhythm.rates")}
									</Title>
									<TimePicker
										label={t("forms.rhythm.hoursPerDay")}
										value={values.hoursPerDay}
										onChange={(value) =>
											setFieldValue(
												"hoursPerDay",
												value ?? "00:00"
											)
										}
									/>
									<InfoMessage color="neutral" hideIcon>
										{t("onboarding.steps.rhythm.weekly", {
											hours,
											days,
										})}
									</InfoMessage>
								</div>
							</FlexGrid>
						</Form>
					</OnboardingStepCard>
				);
			}}
		</Formik>
	);
};
